import React from 'react';
import moment from 'moment';
import Popover from 'antd/es/popover';
import LinesEllipsis from 'react-lines-ellipsis';
import responsiveHOC from 'react-lines-ellipsis/lib/responsiveHOC';

import Text from 'antd/es/typography/Text';
import Tooltip from 'antd/es/tooltip';
import { Header5 } from 'components/layout/headers';
import { TableTitleContainer } from 'components/table/elements';
import { ExclamationIcon } from './icons/exclamation-icon';
import { PathCreator, RoutePaths } from 'routing/constants';
import { TableRateHeaderContainer, TableLink } from './elements';
import { ServerTimeFormat, TimeFormat } from 'utils/constants';
import { renderMoney, renderText } from 'components/table/renders';
import { GolfCardIcon } from '../booking-details-page/players-info/player-info-blocks/icons/golf-card-icon';
import { GolfAssistantIcon } from '../booking-details-page/players-info/player-info-blocks/icons/golf-assistant-icon';
import { GolfEquipmentIcon } from '../booking-details-page/players-info/player-info-blocks/icons/golf-equipment-icon';

import type { IRate } from 'modules/api-requests/rates/types';
import type { ITeeTime } from 'modules/api-requests/tee-time/types';

export const boldTitleWrapper = (node: React.ReactNode) => <TableTitleContainer>{node}</TableTitleContainer>;

const ResponsiveEllipsis = responsiveHOC()(LinesEllipsis);

export const renderRateTitle = (rate: IRate) => {
  const popoverContent = (
    <div style={{ width: '320px' }}>
      <Header5>{rate.name}</Header5>
      <p>{rate.description}</p>
      {rate.options?.length ? (
        <>
          <div>
            <Text strong>Rate includes:</Text>
          </div>
          {rate?.options.map((x) => {
            if (x.name.toLowerCase().indexOf('cart') >= 0)
              return (
                <Tooltip key={x.name} title={x.name}>
                  <GolfCardIcon />
                </Tooltip>
              );
            if (x.name.toLowerCase().indexOf('assistant') >= 0)
              return (
                <Tooltip key={x.name} title={x.name}>
                  <GolfAssistantIcon />
                </Tooltip>
              );
            if (x.name.toLowerCase().indexOf('golf') >= 0)
              return (
                <Tooltip key={x.name} title={x.name}>
                  <GolfEquipmentIcon />
                </Tooltip>
              );
            return null;
          })}
        </>
      ) : null}
    </div>
  );

  return boldTitleWrapper(
    <TableRateHeaderContainer>
      <ResponsiveEllipsis
        maxLine={4}
        ellipsis="..."
        text={rate.name}
        trimRight={true}
        basedOn="letters"
        className="trimmed"
      />

      <Popover content={popoverContent} trigger="hover" placement="bottom">
        <span className="rateHeaderIcon">
          <ExclamationIcon />
        </span>
      </Popover>
    </TableRateHeaderContainer>
  );
};

export const renderRange = (maxRange: number) => renderText(`1 - ${maxRange}`);

export const renderRateLink = (rateId: string) => (ratePrice: number | null | undefined, row: ITeeTime) => {
  return Boolean(ratePrice) || ratePrice === 0 ? (
    <TableLink
      key={row.startTime}
      to={PathCreator[RoutePaths.BOOKING_DETAILS].getUrl({
        date: row.date,
        time: row.startTime,
        rate: rateId,
      })}
    >
      {renderMoney(ratePrice as number)}
    </TableLink>
  ) : (
    renderText('N/A')
  );
};

export const renderPlayersCount = (value: number | number[]) => renderText(formatSlotRange(value));

export const renderTime = (time: string) => {
  return <span>{moment(time, ServerTimeFormat).format(TimeFormat)}</span>;
};

function formatSlotRange(slots: number | number[]): string {
  if (Array.isArray(slots)) {
    if (!slots.length) return '-';
    if (slots.length === 1) return String(slots[0]);

    const sorted = [...slots].sort((a, b) => a - b);
    const isConsecutive = sorted.every((v, i, arr) => i === 0 || v === arr[i - 1] + 1);
    return isConsecutive ? `${sorted[0]}-${sorted[sorted.length - 1]}` : sorted.join(', ');
  } else {
    return [0, 1].includes(slots) ? String(slots) : `1-${slots}`;
  }
}

